import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentUserProfile, getMyTeamMembers, getProfilePictureUrl } from '../services/api';
import ProfilePicture from './ProfilePicture';
import TeamMemberPlanningModal from './TeamMemberPlanningModal';

const EmployeeTeamPage = () => {
  const { t } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  const [currentUser, setCurrentUser] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const userData = await getCurrentUserProfile();
        setCurrentUser(userData);

        if (userData.team && userData.team.id) {
          try {
            const members = await getMyTeamMembers();
            const teamMembersList = Array.isArray(members) ? members : (members.results || []);
            setTeamMembers(teamMembersList);
          } catch (err) {
            console.error('Failed to fetch team members:', err);
            setTeamMembers([]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        setError(t('team.loadFailedInfo'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const manager = currentUser?.team?.manager || currentUser?.manager;

  const getUserInitials = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.first_name) {
      return user.first_name[0].toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  const getUserDisplayName = (user) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email || t('team.unknown');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-primary mb-4"></div>
          <p className="text-gray-600 dark:text-slate-400">{t('team.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !currentUser) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <span className="material-symbols-outlined text-red-500 dark:text-red-400 text-4xl mb-4">error</span>
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">{t('team.title')}</h1>
        <p className="text-gray-600 dark:text-slate-400">
          {currentUser?.team?.name ? (
            <>{t('team.viewTeamIn')} <span className="font-semibold">{currentUser.team.name}</span></>
          ) : (
            t('team.noTeamYet')
          )}
        </p>
      </div>

      {manager ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-600 p-6">
          <div className="flex items-center gap-4 mb-4">
            <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-2xl">supervisor_account</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">{t('team.manager')}</h2>
          </div>
          
          <div className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-100 dark:border-blue-700/50">
            <ProfilePicture
              profilePictureUrl={manager.profile_picture ? getProfilePictureUrl(manager.profile_picture) : null}
              initials={getUserInitials(manager)}
              size="lg"
            />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                {getUserDisplayName(manager)}
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400">{manager.email}</p>
              {manager.role_display && (
                <span className="inline-block mt-2 px-3 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-full">
                  {manager.role_display}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-600 p-6">
          <div className="flex items-center gap-4 mb-4">
            <span className="material-symbols-outlined text-gray-400 dark:text-slate-500 text-2xl">supervisor_account</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">{t('team.manager')}</h2>
          </div>
          <p className="text-gray-500 dark:text-slate-400 italic">{t('team.noManager')}</p>
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-600 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-2xl">groups</span>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">{t('team.teamMembers')}</h2>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                {teamMembers.length === 1
                  ? t('team.memberOne', { count: teamMembers.length })
                  : t('team.memberMany', { count: teamMembers.length })}
              </p>
            </div>
          </div>
        </div>

        {teamMembers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamMembers.map((member) => (
              <button
                type="button"
                key={member.id}
                onClick={() => setSelectedMember(member)}
                className="flex items-center gap-4 p-4 text-left bg-gray-50 dark:bg-slate-700/50 rounded-lg border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 hover:border-blue-300 dark:hover:border-blue-500 transition-colors cursor-pointer w-full"
              >
                <ProfilePicture
                  profilePictureUrl={member.profile_picture ? getProfilePictureUrl(member.profile_picture) : null}
                  initials={getUserInitials(member)}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100 truncate">
                    {getUserDisplayName(member)}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-slate-400 truncate">{member.email}</p>
                  {member.role_display && (
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-full">
                      {member.role_display}
                    </span>
                  )}
                </div>
                <span className="material-symbols-outlined text-gray-400 dark:text-slate-500 shrink-0" aria-hidden="true">
                  calendar_month
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <span className="material-symbols-outlined text-gray-400 dark:text-slate-500 text-4xl mb-2">person_off</span>
            <p className="text-gray-500 dark:text-slate-400 mb-2">{t('team.noOtherMembers')}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 italic">
              {currentUser?.team?.id ? t('team.onlyMember') : t('team.noTeamYet')}
            </p>
          </div>
        )}
      </div>

      <TeamMemberPlanningModal
        isOpen={!!selectedMember}
        member={selectedMember}
        onClose={() => setSelectedMember(null)}
      />
    </div>
  );
};

export default EmployeeTeamPage;
